import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useCallback } from 'react';

import MobileNavLinkList, { MobileNavLinkListItem } from './MobileNavLinkList';
import MobileNavListItem from './MobileNavListItem';
import {
  HeaderLogoFragmentFragment,
  HeaderMenuItemFragmentFragment,
  MenuButtonFragmentFragment,
} from '../../../__generated__/graphql';
import IconButton from '../../../components/IconButton';
import Close from '../../../components/icons/Close';
import Menu from '../../../components/icons/Menu';
import Svg from '../../../components/Svg';
import { Paragraph, TextLink } from '../../../components/Text';
import { convertHref } from '../../../utils/convertHref';
import { removeTrailingSlash } from '../../../utils/removeTrailingSlash';
import { menuButtonFragmentToMenuButtonItems } from '../helpers';

import style from './mobile-nav.module.css';

interface MobileNavProps {
  className: string;
  headerLogo: HeaderLogoFragmentFragment | undefined;
  menuButton: MenuButtonFragmentFragment;
  menuItems: HeaderMenuItemFragmentFragment[];
}

const headerButtonId = 'header-login';

const MobileNav = (props: MobileNavProps) => {
  const router = useRouter();

  const menuButtonItems = menuButtonFragmentToMenuButtonItems(props.menuButton);

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [openMenuId, setOpenMenuId] = React.useState<null | string>(null);

  const toggleMenu = () => {
    setIsMenuOpen(prev => {
      if (prev) {
        setOpenMenuId(null);
      }

      return !prev;
    });
  };

  const handleSetOpenMenuId: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = useCallback(evt => {
    const menuId = evt.currentTarget.getAttribute('data-menu-id');

    if (menuId) {
      setOpenMenuId(menuId === openMenuId ? null : menuId);
    }
    else {
      setOpenMenuId(null);
    }
  }, [openMenuId]);

  return (
    <div className={classNames(style.mobileNav, props.className)}>
      {props.headerLogo
        ? (
          <Link
            aria-label="Navigate to Home"
            className={style.logoLink}
            href="/"
          >
            <Svg
              image={{
                node: {
                  ...props.headerLogo,
                },
              }}
              priority
            />
          </Link>
        )
        : <div />}

      <IconButton
        aria-label="Toggle menu"
        className={style.menuIconButton}
        onClick={toggleMenu}
        round={false}
      >
        {isMenuOpen ? <Close iconColor="white" /> : <Menu />}
      </IconButton>

      <div
        className={classNames(
          style.menu,
          {
            [style.menuOpen]: isMenuOpen,
          },
        )}
      >
        <nav>
          <ul>
            {props.menuItems.map(item => {
              const navDetails = item.navDetails;

              const isMainContentLinkActive = navDetails?.link
                ? removeTrailingSlash(convertHref(navDetails.link.url)) === removeTrailingSlash(router.asPath)
                : false;

              return (
                <MobileNavListItem
                  itemId={item.id}
                  itemLabel={item.label ?? ''}
                  key={item.id}
                  onSetOpenMenuId={handleSetOpenMenuId}
                  openMenuId={openMenuId}
                >
                  <Paragraph
                    className={classNames({ [style.descriptionWithoutMainContentLink]: !navDetails?.link })}
                    fontSize={16}
                  >
                    {navDetails?.description}
                  </Paragraph>

                  <TextLink
                    className={style.mainContentLink}
                    fontSize={16}
                    fontWeight={700}
                    linkFragment={navDetails?.link}
                    onClick={isMainContentLinkActive ? toggleMenu : undefined}
                    underlined
                  />

                  <div className={style.menuLinks}>
                    {item.navSegments?.segments?.map(segment => (
                      <MobileNavLinkList
                        items={segment?.links?.map((link): MobileNavLinkListItem => ({
                          href: link?.link?.url ?? '',
                          label: link?.link?.title ?? '',
                        })) ?? []}
                        key={segment?.title}
                        onClickActive={toggleMenu}
                        sectionTitle={segment?.title}
                      />
                    ))}
                  </div>
                </MobileNavListItem>
              );
            })}

            <MobileNavListItem
              itemId={headerButtonId}
              itemLabel="Log In"
              onSetOpenMenuId={handleSetOpenMenuId}
              openMenuId={openMenuId}
            >
              <div className={style.menuLinks}>
                <MobileNavLinkList
                  items={menuButtonItems.map((subItem): MobileNavLinkListItem => ({
                    ...subItem,
                  }))}
                  onClickActive={toggleMenu}
                />
              </div>
            </MobileNavListItem>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default MobileNav;
